import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Divider,
} from '@mui/material';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../utils/redux/redux';
import { useSaveAssistant } from '../../hooks/useSaveAssistant';
import { useUser } from '../../hooks/useUser';
import { useAgents } from '../../hooks/useAgents';
import React from 'react';
import {
  clearAllState,
  updateCallStages,
} from '../../utils/redux/slices/userJourneySlice';
import { clearAllStates } from '../../utils/redux/slices/reservationSlice';
import { clearAllState as clearGeneralSupportState } from '../../utils/redux/slices/generalSupportSlice';
import { clearAllStates as clearReservationState } from '../../utils/redux/slices/reservationSlice';
import { useNavigate, useLocation } from 'react-router';

const InitialAnalytics = ({
  onPrev,
  shouldShowMenuConfigFlow,
}: {
  onPrev?: () => void;
  shouldShowMenuConfigFlow?: boolean;
}) => {
  const dispatch = useDispatch();
  const { assistantDefinition, menuConfig, personalization } = useSelector(
    (state: RootState) => state.userJourney
  );

  const { faqSetup } = useSelector((state: RootState) => state.generalSupport);
  const navigate = useNavigate();
  const { user } = useUser();
  const { agents } = useAgents();
  const location = useLocation();

  const { saveAssistant, isPending } = useSaveAssistant();
  // Add state for managing rows with their values
  const [rows, setRows] = React.useState([
    { id: 1, stage: '', description: '', objective: '' },
  ]);

  const handleAddRow = () => {
    setRows([
      ...rows,
      { id: rows.length + 1, stage: '', description: '', objective: '' },
    ]);
  };

  // Handle input changes and dispatch to Redux
  const handleInputChange = (
    rowId: number,
    field: 'stage' | 'description' | 'objective',
    value: string
  ) => {
    const updatedRows = rows.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    );
    setRows(updatedRows);

    // Create call stages array and dispatch
    const validStages = updatedRows
      .filter(row => row.stage || row.description || row.objective)
      .map(row => ({
        stage: row.stage,
        description: row.description,
        objective: row.objective as 'inbound' | 'outbound',
      }));

    dispatch(updateCallStages(validStages));
  };

  const isAllFieldsFilled = () => {
    // Check Assistant Definition
    const assistantDef = assistantDefinition.data;
    if (
      !assistantDef.assistantName ||
      !assistantDef.personality ||
      !assistantDef.language ||
      !assistantDef.voice ||
      !assistantDef.timezone
    ) {
      return false;
    }

    // Only validate menuConfigs if there's no POS integration
    if (shouldShowMenuConfigFlow) {
      const menuConfigs = menuConfig.data;
      if (!menuConfigs.menuSettingMethod) {
        return false;
      }
    }

    // Check Personalization
    const personal = personalization.data;
    if (!personal.greeting || !personal.identity || !personal.job) {
      return false;
    }

    return true;
  };

  const isGeneralSupportAllFieldsFilled = () => {
    // Check Assistant Definition
    const assistantDef = assistantDefinition.data;
    if (
      !assistantDef.assistantName ||
      !assistantDef.personality ||
      !assistantDef.language ||
      !assistantDef.voice ||
      !assistantDef.timezone
    ) {
      return false;
    }
    // Check Personalization
    const personal = personalization.data;
    if (!personal.greeting || !personal.identity || !personal.job) {
      return false;
    }

    // Update the FAQ validation to check both preference and status
    const faqSetupStatus = faqSetup.status;
    if (!faqSetupStatus.isComplete || faqSetupStatus.hasError) {
      return false;
    }

    return true;
  };

  const isFieldsFilled = () => {
    // Check if we're in the general support flow
    const isGeneralSupportFlow = location.pathname.includes('general-queries');

    if (isGeneralSupportFlow) {
      return isGeneralSupportAllFieldsFilled();
    } else {
      return isAllFieldsFilled();
    }
  };

  const handleSaveAssistant = async () => {
    try {
      await saveAssistant({
        userId: user?._id,
        assistantId: agents?.agents[agents.agents.length - 1]._id,
        status: 'complete',
        section: 'analytics',
      });
      navigate(`/agent/${agents?.agents[agents.agents.length - 1].agentName}`);
      dispatch(clearAllStates());
      dispatch(clearAllState());
      dispatch(clearGeneralSupportState());
      dispatch(clearReservationState());
      // Handle success - maybe show a success message or redirect
    } catch (error) {
      console.error('Error saving assistant:', error);
      // Handle error - show error message
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '768px',
          mx: 'auto',
          height: 'calc(100vh - 100px)',
          backgroundColor: 'white',
          px: 3,
          py: 2,
          position: 'relative',
        }}
      >
        {/* Header Section */}
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: '18px', color: 'custom.textBlue' }}
          >
            Define Your Call Stages
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              mb: 1,
              fontSize: '12px',
              fontWeight: 400,
              color: 'lightGray.text',
            }}
          >
            Set up your call stages to track assistant interactions effectively
          </Typography>
        </Box>

        <Divider />

        {/* Call Stages Grid Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            mt: 3,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Call Stage
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Description
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Objective
          </Typography>
        </Box>

        {/* Call Stages Rows */}
        {rows.map(row => (
          <Box
            key={row.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              placeholder="Enter Stage"
              size="small"
              value={row.stage}
              onChange={e => handleInputChange(row.id, 'stage', e.target.value)}
              sx={{
                bgcolor: 'white',
                '& .MuiInputBase-input': { fontSize: '14px' },
              }}
            />
            <TextField
              placeholder="Enter Description"
              size="small"
              value={row.description}
              onChange={e =>
                handleInputChange(row.id, 'description', e.target.value)
              }
              sx={{
                bgcolor: 'white',
                height: '34px',
                '& .MuiInputBase-input': { fontSize: '14px' },
              }}
            />
            <Select
              displayEmpty
              size="small"
              value={row.objective}
              onChange={e =>
                handleInputChange(row.id, 'objective', e.target.value)
              }
              sx={{
                bgcolor: 'white',
                height: '37px',
                '& .MuiSelect-select': { fontSize: '14px' },
              }}
            >
              <MenuItem disabled value="">
                Select Objective
              </MenuItem>
              <MenuItem value="inbound">Inbound</MenuItem>
              <MenuItem value="outbound">Outbound</MenuItem>
            </Select>
          </Box>
        ))}

        {/* Add More Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            onClick={handleAddRow}
            sx={{
              mb: 2,
              color: 'primary.main',
              textTransform: 'none',
            }}
          >
            + Add More Fields
          </Button>
        </Box>
      </Box>
      {/* Bottom Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          p: 1,
          borderTop: '1px solid',
          borderColor: 'lightGray.main',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CaretLeft size={16} />}
          sx={{
            height: '34px',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            color: 'custom.textBlue',
            backgroundColor: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
          onClick={onPrev}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          endIcon={<CaretRight size={16} />}
          sx={{
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
          disabled={!isFieldsFilled() || isPending}
          onClick={handleSaveAssistant}
        >
          {isPending ? 'Saving...' : 'Save Agent'}
        </Button>
      </Box>
    </Box>
  );
};

export default InitialAnalytics;
